// Simplified permissions system
// Roles: super_admin (everything), admin (per-page), customer (no dashboard)
// Permissions array stores page keys: ["dashboard", "analytics", "stations", etc.]

export const PAGES = {
  dashboard:   { key: 'dashboard',   label: 'Dashboard',    icon: '🏠', description: 'Overview stats and quick actions' },
  analytics:   { key: 'analytics',   label: 'Analytics',    icon: '📊', description: 'Detailed charging and revenue analytics' },
  users:       { key: 'users',       label: 'Users',        icon: '👥', description: 'View and manage registered users' },
  stations:    { key: 'stations',    label: 'Stations',     icon: '⚡', description: 'Manage chargers and OCPP status' },
  issues:      { key: 'issues',      label: 'Issues',       icon: '🚩', description: 'Charger issues reported by riders' },
  billing:     { key: 'billing',     label: 'Billing',      icon: '💰', description: 'Revenue, invoices, and billing settings' },
  subscribers: { key: 'subscribers', label: 'Mailing List', icon: '📧', description: 'Subscriber list and CSV export' },
  orders:      { key: 'orders',      label: 'Orders',       icon: '🛒', description: 'Home charger order management' },
  careers:     { key: 'careers',     label: 'Careers',      icon: '💼', description: 'Job listings and applications' },
  emails:      { key: 'emails',      label: 'Email Campaigns', icon: '📤', description: 'Send marketing emails to subscribers' },
  notifications: { key: 'notifications', label: 'Notifications', icon: '🔔', description: 'Send push notifications to app users' },
  b2b:         { key: 'b2b',         label: 'B2B Companies', icon: '🏢', description: 'Manage B2B company accounts, chargers, and billing' },
  messages:    { key: 'messages',    label: 'Messages',     icon: '✉️', description: 'Contact form submissions' },
  'home-chargers': { key: 'home-chargers', label: 'Home Chargers', icon: '🔌', description: 'View and manage customer home charger registrations' },
};

// Pages restricted to super_admin regardless of an admin's individual
// permission grants — CR9: notifications and email campaigns can reach every
// user/subscriber at once; CR10: B2B company admin can create/delete any
// company and its billing config. Per-page admin permissions don't apply here.
const SUPER_ADMIN_ONLY_PAGES = ['emails', 'notifications', 'b2b'];

// Check if user can access a specific page
export const canAccess = (user, pageKey) => {
  if (!user) return false;

  // Super admin sees everything
  if (user.role === 'super_admin') return true;

  // Customers never see dashboard
  if (user.role === 'customer' || !user.role) return false;

  // Admin — check specific permissions
  const perms = Array.isArray(user.permissions) ? user.permissions : [];
  if (perms.includes('all')) return true;
  if (SUPER_ADMIN_ONLY_PAGES.includes(pageKey)) return false;
  return perms.includes(pageKey);
};

// Check if user is super admin
export const isSuperAdmin = (user) => {
  return user?.role === 'super_admin';
};

// Check if user is any kind of admin (can access dashboard)
export const isAdmin = (user) => {
  return user?.role === 'super_admin' || user?.role === 'admin';
};

// Legacy compatibility — hasPermission still works
export const hasPermission = (user, requiredPermissions) => {
  if (!user) return false;
  if (user.role === 'super_admin') return true;
  
  const perms = Array.isArray(user.permissions) ? user.permissions : [];
  if (perms.includes('all')) return true;
  
  const required = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
  return required.some(p => perms.includes(p));
};

const permissionsExport = { PAGES, canAccess, isSuperAdmin, isAdmin, hasPermission };
export default permissionsExport;
